import { TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/design/components/alert';
import { Button } from '@/design/components/button';

/**
 * What an operator sees when `GET /api/v1/config` could not be answered — or, once it has, when the
 * session this terminal may hold could not be asked about (`reason="session"`).
 *
 * The second is not the sign-in screen, and that is the point of it: a reload during a brief rate
 * limit or an api restart used to show the sign-in form to somebody who was signed in. It says what
 * is wrong, how long the server asked to be left alone when it said, and offers to try again.
 *
 * It is a full screen with one action, and it exists so that a half-configured application is never
 * the alternative. Defaulting the issuance mode would produce a registration screen that is wrong
 * in one direction with nothing failing anywhere; defaulting the reporting timezone would render
 * every time in the browser's own. Both are silent, and both are worse than saying plainly that the
 * service could not be reached.
 *
 * It renders in whatever language the terminal remembers, or in the product default, because the
 * installation's own default is one of the values that could not be fetched.
 */
export function ConfigurationUnavailable({
  onRetry,
  reason = 'configuration',
  retryAfterSeconds = null,
}: Readonly<{
  onRetry: () => void;
  reason?: 'configuration' | 'session';
  retryAfterSeconds?: number | null;
}>): ReactNode {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-6">
      <div className="flex w-full max-w-lg flex-col gap-4">
        <Alert variant="destructive" role="alert">
          <TriangleAlert aria-hidden />
          <AlertTitle>
            {t(reason === 'session' ? 'config.sessionUnavailableTitle' : 'config.failedTitle')}
          </AlertTitle>
          <AlertDescription>
            {t(reason === 'session' ? 'config.sessionUnavailableBody' : 'config.failedBody')}
            {retryAfterSeconds === null
              ? null
              : ` ${t('errors.retryAfter', { retryAfterSeconds })}`}
          </AlertDescription>
        </Alert>
        <Button onClick={onRetry} className="self-start">
          {t('common.retry')}
        </Button>
      </div>
    </div>
  );
}
